import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowRight, Package, HandHeart, ShoppingBag, Wrench, GraduationCap, Bird, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEO from "../components/SEO";

export const CATEGORY_META = {
    "goods": { label: "Goods", icon: ShoppingBag },
    "services": { label: "Services", icon: HandHeart },
    "equipment-loan": { label: "Equipment loan", icon: Wrench },
    "skills-tutoring": { label: "Skills / Tutoring", icon: GraduationCap },
    "animal-care": { label: "Animal care", icon: Bird },
    "other": { label: "Other", icon: MoreHorizontal },
};

const ServiceCard = ({ svc }) => {
    const Icon = (CATEGORY_META[svc.category] || CATEGORY_META.other).icon;
    return (
        <Link
            to={`/services/${svc.id}`}
            data-testid={`service-card-${svc.id}`}
            className="group bg-white border border-black/10 rounded-2xl p-5 hover:border-[#E53935]/40 hover:shadow-md transition flex flex-col"
        >
            {svc.images?.[0] ? (
                <img src={svc.images[0]} alt={svc.title} className="w-full h-32 object-cover rounded-xl mb-4" />
            ) : (
                <div className="w-full h-32 rounded-xl bg-[#F2D8D5]/50 flex items-center justify-center mb-4">
                    <Icon className="w-10 h-10 text-[#E53935]" strokeWidth={1.2} />
                </div>
            )}
            <div className="flex items-center justify-between mb-2">
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${svc.kind === "request" ? "bg-amber-50 text-amber-800" : "bg-green-100 text-green-800"}`}>
                    {svc.kind === "request" ? "Need" : "Free"}
                </span>
                <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">{(CATEGORY_META[svc.category] || CATEGORY_META.other).label}</span>
            </div>
            <h3 className="font-heading text-lg text-[#1C252E] leading-snug group-hover:text-[#E53935] transition-colors line-clamp-2">{svc.title}</h3>
            <p className="text-xs text-[#4A5568] mt-1 line-clamp-2">{svc.description}</p>
            <div className="flex items-center justify-between mt-auto pt-3 text-xs">
                <span className="text-[#4A5568]">{svc.quantity_available} {svc.unit_label} left</span>
                <span className="text-[#1C252E]">{svc.lister_first_name}{svc.town ? `, ${svc.town}` : ""}</span>
            </div>
            <p className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#E53935] group-hover:gap-2 transition-all">
                View details <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </p>
        </Link>
    );
};

const Services = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [kind, setKind] = useState("all");      // all | offer | request
    const [category, setCategory] = useState("");

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (kind !== "all") params.kind = kind;
        if (category) params.category = category;
        api.get("/services", { params })
            .then((r) => setRows(r.data || []))
            .catch(() => toast.error("Could not load services"))
            .finally(() => setLoading(false));
    }, [kind, category]);

    const offerCount = useMemo(() => rows.filter((r) => r.kind === "offer").length, [rows]);
    const requestCount = useMemo(() => rows.filter((r) => r.kind === "request").length, [rows]);

    return (
        <div className="min-h-screen bg-[#FDFBF7]">
            <SEO title="Available services" description="A free community board: neighbors offering goods, services, equipment loans, and skills — and asking for help when they need it." path="/services" />
            <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
                <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
                    <div>
                        <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Community board · everything free</p>
                        <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05]">Neighbors helping neighbors</h1>
                        <p className="text-base text-[#4A5568] mt-4 max-w-xl">A moderated, no-money board for the Reddog community. Offer goods or services, post a need, claim what you need — all free, no money changes hands here.</p>
                    </div>
                    <Link to="/services/submit" data-testid="services-add-btn" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium transition self-start">
                        <Plus className="w-4 h-4" strokeWidth={2} /> Post an offer or need
                    </Link>
                </header>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mb-8 flex-wrap">
                    <div className="inline-flex bg-white border border-black/10 rounded-full p-1" role="tablist" aria-label="Filter by kind">
                        {[
                            { v: "all", label: `Everything (${rows.length})` },
                            { v: "offer", label: `Free offers (${offerCount})` },
                            { v: "request", label: `Needs (${requestCount})` },
                        ].map((opt) => (
                            <button
                                key={opt.v}
                                data-testid={`services-kind-${opt.v}`}
                                onClick={() => setKind(opt.v)}
                                role="tab"
                                aria-selected={kind === opt.v}
                                className={`px-4 py-1.5 text-sm rounded-full transition ${kind === opt.v ? "bg-[#E53935] text-white font-medium" : "text-[#4A5568] hover:text-[#1C252E]"}`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        data-testid="services-category-select"
                        className="rounded-full border border-black/10 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                    >
                        <option value="">All categories</option>
                        {Object.entries(CATEGORY_META).map(([slug, m]) => (
                            <option key={slug} value={slug}>{m.label}</option>
                        ))}
                    </select>
                </div>

                {loading ? (
                    <p className="text-sm text-[#4A5568]">Loading…</p>
                ) : rows.length === 0 ? (
                    <div className="bg-white border border-black/10 rounded-2xl p-12 text-center" data-testid="services-empty">
                        <Package className="w-12 h-12 text-[#4A5568] mx-auto mb-3" strokeWidth={1.2} />
                        <p className="font-heading text-xl text-[#1C252E]">Board is empty — for now.</p>
                        <p className="text-sm text-[#4A5568] mt-2 max-w-md mx-auto">No matching listings right now. Be the first to share something — post wood, eggs, snow plowing, tutoring, anything.</p>
                        <Link to="/services/submit" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium mt-5">
                            <Plus className="w-4 h-4" strokeWidth={2} /> Post the first listing
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="services-list">
                        {rows.map((s) => <ServiceCard key={s.id} svc={s} />)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Services;
